import React, { useState, useEffect } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import type {
  BillingMode,
  BillingStrategy,
  TicketTypeTemplate,
} from '../../../types/ticketTypeTemplate';
import { slugify } from '../../../utils/slugify';
import { HelpHint } from '../HelpHint';
import { SmartSelect, type SmartSelectOption } from '../../dropdown';
import { FIELD_SX, PRIMARY_BTN_SX } from '../../../styles/designSystem';

// Hover-help text for each field on the create dialog. Lives next to
// the component because the wording is tightly coupled to how each
// field is consumed downstream.
const HELP = {
  displayName:
    'Human-friendly name shown to techs in pickers and headings ' +
    '(e.g. "Microsoft 365 Onboarding"). Auto-seeds the Ticket Type ID; ' +
    'editing the ID after will detach the two so renames don\'t ' +
    'silently change the slug.',
  ticketTypeId:
    'Stable identifier used in URLs, routing, and as the foreign key ' +
    'from work items and task configs (snake_case, e.g. m365_onboarding). ' +
    'Avoid changing after creation — existing tickets and templates ' +
    'reference it directly.',
  group:
    'Category used to group this template in the ticket-type picker ' +
    '(e.g. "Onboarding", "Repair", "Project"). Pick an existing group ' +
    'to slot it next to siblings, or type a new one to start a group.',
  serviceKey:
    'Controls which customers see this template. When raising a ticket, ' +
    'the picker only shows templates whose serviceKey matches one of ' +
    'the customer\'s ACTIVE services (e.g. "M365", "Google", "Backup", ' +
    '"Hosting"). Match is case-insensitive. ' +
    'Use the special value "other" to always show this template ' +
    'regardless of customer services — for one-off ticket types that ' +
    'aren\'t tied to a subscribed service.',
  billingMode:
    'How time/effort is captured for invoicing. "hours" — bill by ' +
    'recorded hours. "units" — bill by discrete units (licences, ' +
    'devices, jobs).',
  billingStrategy:
    'Pricing rule applied at invoice time. "none" — bill flat from ' +
    'line items. "remote-support" — applies the remote-support rate ' +
    'matrix. "fixed-labour" — caps labour at a fixed amount. ' +
    '"per-license" — multiplies by the licence quantity on the ticket.',
} as const;

interface CreateTicketTypeTemplateDialogProps {
  open: boolean;
  existingIds: string[];
  existingGroups: string[];
  onClose: () => void;
  onCreate: (template: TicketTypeTemplate) => void;
}

const BILLING_MODE_OPTIONS: SmartSelectOption[] =
  (['hours', 'units'] as BillingMode[]).map((m) => ({ value: m, label: m }));
const BILLING_STRATEGY_OPTIONS: SmartSelectOption[] =
  (['none', 'remote-support', 'fixed-labour', 'per-license'] as BillingStrategy[]).map((s) => ({ value: s, label: s }));

// Comfortable-tier (40px/14px) SmartSelect trigger — scoped to this dialog.
const SELECT_SX = { flex: 1, '& .sm4rt-select__trigger': { height: 40, fontSize: 14 } } as const;

export const CreateTicketTypeTemplateDialog: React.FC<CreateTicketTypeTemplateDialogProps> = ({
  open,
  existingIds,
  existingGroups,
  onClose,
  onCreate,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [ticketTypeId, setTicketTypeId] = useState('');
  const [group, setGroup] = useState('');
  const [serviceKey, setServiceKey] = useState('');
  const [billingMode, setBillingMode] = useState<BillingMode>('hours');
  const [billingStrategy, setBillingStrategy] = useState<BillingStrategy>('none');
  const [idManuallyEdited, setIdManuallyEdited] = useState(false);

  useEffect(() => {
    if (!open) {
      setDisplayName('');
      setTicketTypeId('');
      setGroup('');
      setServiceKey('');
      setBillingMode('hours');
      setBillingStrategy('none');
      setIdManuallyEdited(false);
    }
  }, [open]);

  const handleNameChange = (value: string) => {
    setDisplayName(value);
    if (!idManuallyEdited) {
      setTicketTypeId(slugify(value).replace(/-/g, '_'));
    }
  };

  const handleIdChange = (value: string) => {
    setTicketTypeId(value);
    setIdManuallyEdited(true);
  };

  const trimmedId = ticketTypeId.trim();
  const trimmedName = displayName.trim();
  const trimmedGroup = group.trim();
  const trimmedServiceKey = serviceKey.trim();
  const isDuplicate = existingIds.includes(trimmedId);
  const isValid =
    trimmedId.length > 0 &&
    trimmedName.length > 0 &&
    trimmedGroup.length > 0 &&
    trimmedServiceKey.length > 0 &&
    !isDuplicate;

  const handleCreate = () => {
    if (!isValid) return;
    onCreate({
      ticketTypeId: trimmedId,
      displayName: trimmedName,
      group: trimmedGroup,
      serviceKey: trimmedServiceKey,
      billingMode,
      billingStrategy,
      templateVersion: '1.0',
      additionalFields: [],
      templateLineItems: [],
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Create Ticket Type Template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" alignItems="flex-start">
            <TextField
              label="Display Name"
              value={displayName}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              fullWidth
              autoFocus
              sx={FIELD_SX}
            />
            <HelpHint text={HELP.displayName} />
          </Stack>
          <Stack direction="row" alignItems="flex-start">
            <TextField
              label="Ticket Type ID"
              value={ticketTypeId}
              onChange={(e) => handleIdChange(e.target.value)}
              required
              fullWidth
              helperText={
                isDuplicate
                  ? 'This ID already exists'
                  : 'Auto-generated from name. Edit to customise.'
              }
              error={isDuplicate}
              slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
              sx={FIELD_SX}
            />
            <HelpHint text={HELP.ticketTypeId} />
          </Stack>
          <Stack direction="row" alignItems="flex-start">
            <Autocomplete
              freeSolo
              options={existingGroups}
              value={group}
              onChange={(_, v) => setGroup(v ?? '')}
              onInputChange={(_, v) => setGroup(v)}
              sx={[{ flex: 1 }, FIELD_SX]}
              renderInput={(params) => <TextField {...params} label="Group" required fullWidth />}
            />
            <HelpHint text={HELP.group} />
          </Stack>
          <Stack direction="row" alignItems="flex-start">
            <TextField
              label="Service Key"
              value={serviceKey}
              onChange={(e) => setServiceKey(e.target.value)}
              required
              fullWidth
              helperText="e.g. M365, Backup, Hosting"
              sx={FIELD_SX}
            />
            <HelpHint text={HELP.serviceKey} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
              <Box sx={SELECT_SX}>
                <SmartSelect
                  label="Billing Mode"
                  value={billingMode}
                  options={BILLING_MODE_OPTIONS}
                  onChange={(v) => setBillingMode((v ?? 'hours') as BillingMode)}
                />
              </Box>
              <HelpHint text={HELP.billingMode} />
            </Stack>
            <Stack direction="row" alignItems="center" sx={{ flex: 1 }}>
              <Box sx={SELECT_SX}>
                <SmartSelect
                  label="Billing Strategy"
                  value={billingStrategy}
                  options={BILLING_STRATEGY_OPTIONS}
                  onChange={(v) => setBillingStrategy((v ?? 'none') as BillingStrategy)}
                />
              </Box>
              <HelpHint text={HELP.billingStrategy} />
            </Stack>
          </Stack>
          {isDuplicate && (
            <Alert severity="error">
              A template with ID "{trimmedId}" already exists.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#888' }}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disableElevation disabled={!isValid} sx={PRIMARY_BTN_SX}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};
